'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { loginAction } from '@/app/(auth)/login/actions';
import Link from 'next/link';

const loginSchema = z.object({
  email: z.string().email('Invalid email format'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

function mapLoginError(message: string): string {
  if (message.includes('Invalid login credentials')) {
    return 'Invalid email or password';
  }
  return message;
}

interface LoginFormProps {
  redirectTo: string;
}

export function LoginForm({ redirectTo }: LoginFormProps) {
  const router = useRouter();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onTouched',
  });

  async function onSubmit(data: LoginFormData) {
    setServerError(null);
    const result = await loginAction(data.email, data.password);
    if (result.error) {
      setServerError(mapLoginError(result.error));
      return;
    }
    router.push(redirectTo);
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} onChange={() => setServerError(null)} aria-describedby={serverError ? 'server-error' : undefined}>
      {serverError && (
        <p id="server-error" role="alert" className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2 text-sm text-red-300">
          {serverError}
        </p>
      )}

      <div className="mb-4">
        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          aria-describedby={errors.email ? 'email-error' : undefined}
          {...register('email')}
        />
        {errors.email && (
          <p id="email-error" role="alert" className="mt-1.5 text-sm text-red-400">
            {errors.email.message}
          </p>
        )}
      </div>

      <div className="mb-6">
        <label htmlFor="password">Password</label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          placeholder="••••••••"
          aria-describedby={errors.password ? 'password-error' : undefined}
          {...register('password')}
        />
        {errors.password && (
          <p id="password-error" role="alert" className="mt-1.5 text-sm text-red-400">
            {errors.password.message}
          </p>
        )}
        <div className="mt-1.5 text-right">
          <Link href="/forgot-password" className="text-sm" style={{ color: '#a78bfa' }}>
            Forgot password?
          </Link>
        </div>
      </div>

      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Signing in...' : 'Sign in'}
      </button>

      <p className="mt-5 text-center text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
        Don&apos;t have an account?{' '}
        <Link href="/signup">Sign up</Link>
      </p>
    </form>
  );
}
